import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';
import { BoolNode } from 'src/services/common.types';

import Iconify from 'src/components/iconify';

import { SearchExplanation } from 'src/services/candidates/candidates.types';

import { FieldOption } from '../types/search.types';
import { getFieldOptions } from '../utils/search.utils';
import { CriteriaRow, dslToRows } from '../utils/criteria-rows';

// ----------------------------------------------------------------------

/**
 * What the AI search is doing, approximated, and could not do (JT1-1618, JT1-1636).
 *
 * Without this, an empty result set is indistinguishable from a broken feature. During JT1-1617's
 * testing every "no results" needed a database query to explain — the term was searched as free
 * text because nobody had that skill, or the constraint had no field at all. The backend already
 * knew all of that and had nowhere to say it.
 *
 * Three sections, in descending order of how much the recruiter can act on them:
 *   searching for — the criteria as they stand, so they can see what is filtering
 *   fell back     — searched, but loosely, and why
 *   unmapped      — not searched at all, so they know to look for it another way
 *
 * The first is DERIVED from the criteria rows rather than from anything the backend said. It used to
 * be a snapshot taken at transform time, which was fine until the rows became editable: the moment
 * the recruiter changed one, the summary described a search that was no longer running — the exact
 * confusion this panel exists to prevent. Deriving it means there is nothing to keep in sync.
 *
 * The other two still come from the explanation. They describe what the AI did with the original
 * text and have no row to follow.
 */

type Props = {
  explanation: SearchExplanation | null;
  /** The criteria as they currently stand, not as they were first interpreted. */
  dsl?: BoolNode | null;
};

/** A row as one line of a sentence: "Role title · is any of · full stack, developer". */
const describeRow = (
  row: CriteriaRow,
  fieldOptions: FieldOption[],
  displayNames: Record<string, string>,
): { label: string; match: string; detail: string } => {
  const field = fieldOptions.find((option) => option.id === row.field);
  const values = Array.isArray(row.value) ? row.value : [row.value];

  return {
    label: field?.label ?? row.field,
    match: row.match,
    // a value the recruiter typed reads as itself; a location token or a label ID does not
    detail: values
      .filter((value) => value !== '' && value !== undefined && value !== null)
      .map((value) => displayNames[String(value)] ?? String(value))
      .join(', '),
  };
};

export default function NLSearchExplanation({ explanation, dsl }: Props) {
  const { t } = useTranslate();

  if (!explanation) return null;

  const { summary, fell_back, unmapped, fell_back_entirely, display_names } = explanation;
  const criteria = dslToRows(dsl ?? null).rows
    .map((row) => describeRow(row, getFieldOptions(), display_names ?? {}))
    .filter((row) => row.detail !== '');

  return (
    <Stack spacing={1.5}>
      {!!summary && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {summary}
        </Typography>
      )}

      {/* the whole query was searched as free text — say so plainly, because the results will look
          arbitrary otherwise */}
      {fell_back_entirely && (
        <Alert severity="warning" icon={<Iconify icon="solar:magnifer-bold-duotone" />}>
          {t('components.advanced_search.ai_explanation_all_free_text')}
        </Alert>
      )}

      {criteria.length > 0 && (
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
            <Iconify
              icon="eva:checkmark-circle-2-fill"
              width={16}
              sx={{ color: 'success.main', flexShrink: 0 }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('components.advanced_search.ai_explanation_searching_for')}
            </Typography>
          </Stack>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {criteria.map((item, index) => (
              <Chip
                // the field is not unique — one search can carry two skills rows
                key={`${item.label}-${index}`}
                size="small"
                variant="soft"
                color="success"
                label={
                  <Typography variant="caption" component="span">
                    <strong>{item.label}</strong>
                    {' '}
                    {t(`components.advanced_search.match_types.${item.match}`)}
                    {`: ${item.detail}`}
                  </Typography>
                }
              />
            ))}
          </Box>
        </Box>
      )}

      {fell_back.length > 0 && (
        <Alert severity="info" icon={<Iconify icon="solar:document-text-bold-duotone" />}>
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
            {t('components.advanced_search.ai_explanation_free_text')}
          </Typography>
          <Stack spacing={0.25}>
            {fell_back.map((item, index) => (
              <Typography key={`${item.text}-${index}`} variant="caption" component="div">
                <strong>{item.text}</strong> — {item.reason}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {unmapped.length > 0 && (
        <Alert severity="warning" icon={<Iconify icon="solar:danger-triangle-bold-duotone" />}>
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
            {t('components.advanced_search.ai_explanation_not_searched')}
          </Typography>
          <Stack spacing={0.25}>
            {unmapped.map((item, index) => (
              <Typography key={`${item.text}-${index}`} variant="caption" component="div">
                <strong>{item.text}</strong> — {item.reason}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
